import { Injectable, OnDestroy } from '@angular/core';
import {
  BillingApiService,
  GeneratedFrequency,
  GetProductSummariesTotalRequestInterface,
  ProductSummaryBillingStrategy,
  ProductSummaryInterface,
  ProductType,
} from '@galaxy/billing';
import { PartnerService } from '@galaxy/partner';
import { GalaxyDataSource, PagedListRequestInterface, PagedResponseInterface } from '@vendasta/galaxy/table';
import { GalaxyColumnDef } from '@vendasta/galaxy/table/src/table.interface';
import { Observable, of, ReplaySubject, Subscription } from 'rxjs';
import { catchError, map, share, skipWhile, switchMap } from 'rxjs/operators';
import { BillingPeriod } from '../../common/billing-period';
import { Discount, LoadRequestBody, Pricing, ProductSummary, Rule } from './product-summary';

@Injectable({ providedIn: 'root' })
export class ProductSummaryService implements OnDestroy {
  constructor(
    private partnerService: PartnerService,
    private billingApiService: BillingApiService,
  ) {
    this.registerLoadRequestSubscription();
  }

  get paginatedApi() {
    return {
      get: (req: PagedListRequestInterface): Observable<PagedResponseInterface<ProductSummary>> => {
        const currentBillingPeriod = BillingPeriod.getCurrentBillingPeriod();
        return this.partnerService.getPartnerId().pipe(
          map((partnerId) => {
            const body: LoadRequestBody = {
              merchantId: partnerId,
              startDate: currentBillingPeriod.startDate.toDate(),
              endDate: currentBillingPeriod.endDate.toDate(),
              cursor: req.pagingOptions.cursor || '',
              pageSize: req.pagingOptions.pageSize || 10,
            };
            return body;
          }),
          switchMap((body) => this.billingApiService.listProductSummaries(body)),
          map((res) => {
            const summaries: ProductSummary[] = this.productSummaries(res.results);
            const pagedSummaries: PagedResponseInterface<ProductSummary> = {
              data: summaries,
              pagingMetadata: {
                hasMore: res.hasMore,
                nextCursor: res.nextCursor,
              },
            };
            return pagedSummaries;
          }),
        );
      },
    };
  }

  get dataSource(): GalaxyDataSource<ProductSummary> {
    return new GalaxyDataSource<ProductSummary>(this.paginatedApi);
  }

  get columns(): GalaxyColumnDef[] {
    return [{ id: 'product' }, { id: 'units' }, { id: 'gross_total' }, { id: 'discount' }, { id: 'net_total' }];
  }

  private loadRequest$$: ReplaySubject<LoadRequestBody> = new ReplaySubject(1);

  private productSummaries$$: ReplaySubject<ProductSummary[]> = new ReplaySubject(1);
  private hasMore$$: ReplaySubject<boolean> = new ReplaySubject(1);
  private nextCursor$$: ReplaySubject<string> = new ReplaySubject(1);
  private totalResults$$: ReplaySubject<number> = new ReplaySubject(1);
  private error$$: ReplaySubject<any> = new ReplaySubject(1);

  private subscriptions: Subscription[] = [];

  get productSummaries$(): Observable<ProductSummary[]> {
    return this.productSummaries$$.asObservable().pipe(share());
  }

  get hasMore$(): Observable<boolean> {
    return this.hasMore$$.asObservable().pipe(share());
  }

  get nextCursor$(): Observable<string> {
    return this.nextCursor$$.asObservable().pipe(share());
  }

  get totalResults$(): Observable<number> {
    return this.totalResults$$.asObservable().pipe(share());
  }

  get error$(): Observable<any> {
    return this.error$$.asObservable().pipe(
      skipWhile((err) => err === null),
      share(),
    );
  }

  public getProductSummariesTotal(merchantId: string, startDate: Date, endDate: Date): Observable<number> {
    const body: GetProductSummariesTotalRequestInterface = {
      merchantId: merchantId,
      startDate: startDate,
      endDate: endDate,
    };
    return this.billingApiService.getProductSummariesTotal(body).pipe(map((response) => response.totalAmount));
  }

  public loadProductSummaries(
    merchantId: string,
    startDate: Date,
    endDate: Date,
    cursor?: string,
    pageSize?: number,
    skus?: string[],
  ): void {
    const body: LoadRequestBody = {
      merchantId: merchantId,
      startDate: startDate,
      endDate: endDate,
      cursor: cursor || '',
      pageSize: pageSize || 0,
      skus: skus || [],
    };
    this.loadRequest$$.next(body);
  }

  private registerLoadRequestSubscription(): void {
    this.subscriptions.push(
      this.loadRequest$$
        .pipe(
          switchMap((body) => this.billingApiService.listProductSummaries(body)),
          catchError((error) => {
            this.error$$.next(error);
            return of(null);
          }),
          map((response) => {
            if (response != null) {
              this.productSummaries$$.next(this.productSummaries(response.results));
              this.hasMore$$.next(response.hasMore);
              this.nextCursor$$.next(response.nextCursor);
              this.totalResults$$.next(response.totalResults);
            }
          }),
        )
        .subscribe(),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  private productSummaries(results: ProductSummaryInterface[]): ProductSummary[] {
    if (results == null) {
      return [];
    }
    const summaries: ProductSummary[] = results.map((s) => ({
      sku: s.sku,
      productName: s.productName,
      totalQuantity: this.parseNumber(s.totalQuantity),
      totalAmount: this.parseNumber(s.totalAmount),
      totalDiscount: this.parseNumber(s.totalDiscount),
      pricing: this.pricing(s.pricing),
      discounts: this.discounts(s.discounts),
      productType: ProductType[s.productType || ProductType.PRODUCT.toString()],
      frequency: GeneratedFrequency[s.frequency || GeneratedFrequency.INVALID.toString()],
      billingStrategy:
        ProductSummaryBillingStrategy[
          s.billingStrategy || ProductSummaryBillingStrategy.CHARGE_AT_END_OF_BILLING_PERIOD.toString()
        ],
    }));
    return summaries;
  }

  private pricing(pricing: any): Pricing | null {
    if (pricing == null) {
      return null;
    }
    return {
      type: this.ruleType(pricing.type),
      rules: this.rules(pricing.rules),
    };
  }

  private ruleType(type: string): string {
    switch (type) {
      case 'STAIRSTEP':
        return 'Stairstep';
      case 'TIERED':
        return 'Tiered';
      default:
        return 'Standard';
    }
  }

  private rules(rules: any[]): Rule[] {
    if (rules == null || rules.length === 0) {
      return [];
    }
    const result: Rule[] = rules.map((rule) => ({
      min: this.parseNumber(rule.min),
      max: this.parseNumber(rule.max),
      units: this.parseNumber(rule.units),
      unitPrice: this.parseNumber(rule.unitPrice),
      totalAmount: this.parseNumber(rule.totalAmount),
    }));
    return result;
  }

  private discounts(discounts: any[]): Discount[] {
    if (discounts == null || discounts.length === 0) {
      return [];
    }
    const result: Discount[] = [];
    discounts.forEach((discount) => {
      result.push({
        type: this.discountType(discount.type),
        value: this.parseNumber(discount.value),
        totalAmount: this.parseNumber(discount.totalAmount),
        description: discount.description,
      });
    });
    return result;
  }

  private discountType(type: string): string {
    switch (type) {
      case 'PERCENT_AMOUNT':
        return 'PERCENT_AMOUNT';
      case 'FIXED_NUMBER_OF_UNITS':
        return 'FIXED_NUMBER_OF_UNITS';
      case 'FIXED_AMOUNT_PER_UNIT':
        return 'FIXED_AMOUNT_PER_UNIT';
      default:
        return 'FIXED_AMOUNT';
    }
  }

  private parseNumber(val: any): number {
    return Number(val || 0);
  }
}
